import React, { useEffect } from 'react';
import type { Reporte } from '@/lib/api';

const ESTADO_LABEL: Record<string, string> = { pendiente: 'Pendiente', proceso: 'En proceso', resuelto: 'Resuelto' };
const PRIORIDAD_LABEL: Record<string, string> = { alta: 'ALTA', media: 'MEDIA', baja: 'BAJA' };

interface Props {
  reportes: Reporte[];
  institucion: string;
  filtroEstado: string | null;
  filtroSector: string | null;
  onClose: () => void;
}

export default function ExportarPDF({ reportes, institucion, filtroEstado, filtroSector, onClose }: Props) {
  const fecha = new Date().toLocaleDateString('es-VE', { day: '2-digit', month: 'long', year: 'numeric' });

  // Resumen
  const total = reportes.length;
  const pendientes = reportes.filter(r => r.estado === 'pendiente').length;
  const enProceso = reportes.filter(r => r.estado === 'proceso').length;
  const resueltos = reportes.filter(r => r.estado === 'resuelto').length;

  // Agrupar por categoría
  const porCategoria: Record<string, Reporte[]> = {};
  reportes.forEach(r => {
    const label = r.categoria.label;
    if (!porCategoria[label]) porCategoria[label] = [];
    porCategoria[label].push(r);
  });

  useEffect(() => {
    const t = setTimeout(() => window.print(), 400);
    const afterPrint = () => onClose();
    window.addEventListener('afterprint', afterPrint);
    return () => { clearTimeout(t); window.removeEventListener('afterprint', afterPrint); };
  }, []);

  return (
    <>
      <style>{`
        @media print {
          body > *:not(#pdf-root) { display: none !important; }
          #pdf-root { display: block !important; }
          @page { margin: 18mm 15mm; size: A4; }
        }
        @media screen {
          #pdf-root {
            position: fixed; inset: 0; z-index: 9999;
            background: #fff; overflow-y: auto;
            padding: 40px 48px;
            font-family: 'Manrope', sans-serif;
          }
        }
        #pdf-root { color: #0a1628; font-size: 12px; line-height: 1.5; }
        .pdf-header { border-bottom: 2px solid #1D4ED8; padding-bottom: 14px; margin-bottom: 20px; }
        .pdf-logo { font-size: 20px; font-weight: 900; color: #1D4ED8; letter-spacing: -0.5px; }
        .pdf-meta { font-size: 10.5px; color: #6b7280; margin-top: 4px; }
        .pdf-section-title { font-size: 10px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #1D4ED8; margin: 22px 0 10px; }
        .pdf-resumen { display: flex; gap: 16px; }
        .pdf-resumen-card { flex: 1; border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 10px 14px; }
        .pdf-resumen-card .num { font-size: 26px; font-weight: 900; color: #0a1628; }
        .pdf-resumen-card .lbl { font-size: 9.5px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .pdf-cat-header { background: #f3f4f6; padding: 7px 12px; border-radius: 6px; font-weight: 800; font-size: 11px; margin: 18px 0 8px; display: flex; justify-content: space-between; align-items: center; }
        .pdf-cat-count { font-size: 9px; background: #1D4ED822; color: #1D4ED8; padding: 2px 8px; border-radius: 20px; font-weight: 700; }
        .pdf-row { border-bottom: 0.5px solid #e5e7eb; padding: 9px 4px; display: flex; gap: 10px; }
        .pdf-row-num { width: 18px; color: #9ca3af; font-size: 10px; flex-shrink: 0; padding-top: 1px; }
        .pdf-row-body { flex: 1; }
        .pdf-row-titulo { font-weight: 700; font-size: 12px; color: #0a1628; }
        .pdf-row-meta { font-size: 10px; color: #6b7280; margin-top: 2px; }
        .pdf-row-desc { font-size: 10px; color: #374151; margin-top: 4px; white-space: pre-wrap; }
        .pdf-badge { display: inline-block; padding: 1px 7px; border-radius: 20px; font-size: 9px; font-weight: 700; margin-right: 4px; }
        .badge-pendiente { background: #fef3c7; color: #b45309; }
        .badge-proceso    { background: #dbeafe; color: #1d4ed8; }
        .badge-resuelto   { background: #dcfce7; color: #15803d; }
        .badge-alta  { background: #fee2e2; color: #b91c1c; }
        .badge-media { background: #fef3c7; color: #b45309; }
        .badge-baja  { background: #dcfce7; color: #15803d; }
        .pdf-toolbar { position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #e5e7eb; padding: 12px 24px; display: flex; gap: 12px; justify-content: flex-end; }
        @media print { .pdf-toolbar { display: none !important; } }
        .pdf-footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 9.5px; color: #9ca3af; display: flex; justify-content: space-between; }
      `}</style>

      <div id="pdf-root">
        {/* Encabezado */}
        <div className="pdf-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="pdf-logo">ComuniData</div>
              <div className="pdf-meta">
                Institución: <strong>{institucion}</strong> &nbsp;·&nbsp; Fecha: {fecha}
                {filtroEstado && <> &nbsp;·&nbsp; Filtro: {ESTADO_LABEL[filtroEstado]}</>}
                {filtroSector && <> &nbsp;·&nbsp; Sector: {filtroSector}</>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: '#0a1628' }}>Reporte de Seguimiento</div>
              <div style={{ fontSize: 9.5, color: '#9ca3af', marginTop: 2 }}>{total} registro{total !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Resumen */}
        <div className="pdf-section-title">Resumen</div>
        <div className="pdf-resumen">
          {[
            { num: total,      lbl: 'Total',      style: {} },
            { num: pendientes, lbl: 'Pendientes',  style: { borderColor: '#fbbf24' } },
            { num: enProceso,  lbl: 'En proceso',  style: { borderColor: '#3b82f6' } },
            { num: resueltos,  lbl: 'Resueltos',   style: { borderColor: '#22c55e' } },
          ].map(c => (
            <div key={c.lbl} className="pdf-resumen-card" style={c.style}>
              <div className="num">{c.num}</div>
              <div className="lbl">{c.lbl}</div>
            </div>
          ))}
        </div>

        {/* Por categoría — resumen rápido */}
        <div className="pdf-section-title">Por categoría</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(porCategoria).map(([cat, items]) => (
            <div key={cat} style={{ fontSize: 10.5, padding: '4px 10px', borderRadius: 20, background: '#f3f4f6', fontWeight: 600 }}>
              {cat}: <strong>{items.length}</strong>
            </div>
          ))}
        </div>

        {/* Listado detallado por categoría */}
        <div className="pdf-section-title" style={{ marginTop: 28 }}>Listado detallado</div>
        {Object.entries(porCategoria).map(([cat, items]) => (
          <div key={cat}>
            <div className="pdf-cat-header">
              <span>{cat}</span>
              <span className="pdf-cat-count">{items.length} reporte{items.length !== 1 ? 's' : ''}</span>
            </div>
            {items.map((r, i) => (
              <div key={r.id} className="pdf-row">
                <div className="pdf-row-num">{i + 1}</div>
                <div className="pdf-row-body">
                  <div className="pdf-row-titulo">{r.titulo}</div>
                  <div className="pdf-row-meta">
                    <span>Sector: <strong>{r.sector}</strong></span>
                    &nbsp;·&nbsp;
                    <span>Brigadista: {r.brigadista}</span>
                    &nbsp;·&nbsp;
                    <span>Fecha: {new Date(r.createdAt).toLocaleDateString('es-VE')}</span>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <span className={`pdf-badge badge-${r.estado}`}>{ESTADO_LABEL[r.estado]}</span>
                    <span className={`pdf-badge badge-${r.prioridad}`}>{PRIORIDAD_LABEL[r.prioridad]}</span>
                  </div>
                  {r.descripcion && (
                    <div className="pdf-row-desc">{r.descripcion}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Pie de página */}
        <div className="pdf-footer">
          <span>ComuniData — Sistema de Gestión Comunitaria</span>
          <span>Generado el {fecha}</span>
        </div>

        {/* Barra de acciones (solo en pantalla) */}
        <div className="pdf-toolbar">
          <button onClick={onClose}
            style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
            Cerrar
          </button>
          <button onClick={() => window.print()}
            style={{ padding: '9px 18px', borderRadius: 10, background: '#1D4ED8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="ti ti-printer" style={{ fontSize: 15 }} />
            Imprimir / Guardar PDF
          </button>
        </div>
      </div>
    </>
  );
}
